<template>
  <!-- Account full-screen sheet (logged in) -->
  <Teleport to="body">
    <div v-if="showAccountSheet" :class="['mobile-account-sheet', accountSheetClass]">
      <div class="mobile-account-sheet__header">
        <span class="mobile-account-sheet__title">{{ t('mobileNav.account') }}</span>
        <button class="mobile-account-sheet__close" @click="showAccountSheet = false">
          <ITablerX width="24" height="24" />
        </button>
      </div>
      <div class="mobile-account-sheet__body">
        <section class="mobile-account-profile">
          <p class="mobile-account-profile__welcome">{{ t('mine.menu.welcome') }}!</p>
          <div class="mobile-account-profile__user">
            <div class="mobile-account-profile__avatar-wrap">
              <img
                v-if="hasAccountAvatar"
                class="mobile-account-profile__avatar"
                :src="accountAvatarUrl"
                alt=""
              />
              <div
                v-else
                class="mobile-account-profile__avatar mobile-account-profile__avatar-fallback"
              >
                {{ accountInitials }}
              </div>
              <el-upload
                v-if="accountAvatarUploadAction"
                class="mobile-account-profile__avatar-upload"
                :action="accountAvatarUploadAction"
                :show-file-list="false"
                :on-success="handleAccountAvatarSuccess"
              >
                <button
                  class="mobile-account-profile__avatar-edit"
                  type="button"
                  aria-label="Edit avatar"
                >
                  <img src="/images/user/pen.jpg" alt="" />
                </button>
              </el-upload>
            </div>
            <div class="mobile-account-profile__meta">
              <p class="mobile-account-profile__name">{{ accountDisplayName }}</p>
              <p v-if="accountMembershipLabel" class="mobile-account-profile__membership">
                <img class="mobile-account-profile__vip" :src="accountMembershipIcon" alt="" />
                <span>{{ accountMembershipLabel }}</span>
              </p>
              <div v-if="isAgentAccount" class="mobile-account-profile__agent">
                <p v-if="accountAgencyId" class="mobile-account-profile__agency-id">
                  {{ t('mine.agencyID') }}: TTG-{{ accountAgencyId }}
                </p>
                <div class="mobile-account-profile__agent-level" aria-label="Agent level">
                  <img
                    v-for="level in 5"
                    :key="level"
                    class="mobile-account-profile__diamond"
                    :src="level <= agentLevel ? '/images/mine/diamond_r.png' : '/images/mine/diamond_b.png'"
                    alt=""
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
        <UserMenu />
      </div>
    </div>
  </Teleport>

  <nav class="mobile-nav" :class="isDark ? 'mobile-nav--dark' : 'mobile-nav--light'">
    <button
      v-for="item in visibleItems"
      :key="item.id"
      class="mobile-nav__item"
      :class="{
        'mobile-nav__item--active': item.active,
        'mobile-nav__item--disabled': item.disabled
      }"
      :disabled="item.disabled"
      @click="item.action()"
    >
      <span class="mobile-nav__icon">
        <img
          v-if="item.id === 'voyada'"
          :class="['mobile-nav__voyada-icon', item.active && 'mobile-nav__voyada-icon--active']"
          :src="voyadaNavIconUrl"
          width="24"
          height="23"
          alt="AI Chat"
        />
        <span
          v-else-if="item.id === 'support' && activeChat === 'support'"
          class="mobile-nav__close-pill"
        >
          <ITablerX width="16" height="16" />
        </span>
        <ITablerHome v-else-if="item.id === 'home'" width="24" height="24" />
        <ITablerShoppingCart v-else-if="item.id === 'cart'" width="24" height="24" />
        <ITablerUser v-else-if="item.id === 'account'" width="24" height="24" />
        <ITablerHeadset v-else-if="item.id === 'support'" width="24" height="24" />
        <span v-if="item.badge" class="mobile-nav__badge">{{ item.badge }}</span>
      </span>
      <span class="mobile-nav__label">{{ item.label }}</span>
    </button>
  </nav>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch, getCurrentInstance } from 'vue'
import { storeToRefs } from 'pinia'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useWindowSize } from '@vueuse/core'
import { useCartStore } from '@/stores/CartStore'
import { useUserStore } from '@/stores/UserStore'
import { useChatStore } from '@/stores/ChatStore'
import { useVoyadaFlag, voyadaHeroMode } from '@/composables/useVoyadaFlag'
import { showMobileLeftMenu, showMobileAccountSheet } from '@/composables/useMobileMenu'
import voyadaNavIconUrl from '@/assets/icon/voyada-nav.svg'
import UserMenu from '@/components/usermenu.vue'
import Request from '@/services/Request'
import { dataUrl } from '@/utils/AssetUtils'

const hasRouter = !!getCurrentInstance()?.appContext.config.globalProperties.$router
const route = hasRouter ? useRoute() : { path: '', fullPath: '', query: {}, params: {}, name: undefined }
const { t } = useI18n()
const cart = useCartStore()
const userStore = useUserStore()
const chatStore = useChatStore()
const { activeChat } = storeToRefs(chatStore)
const { toggleVoyada, toggleSupport, onZohoClosed, onVoyadaClosed, onVoyadaOpened } = chatStore
const { enabled: voyadaEnabled } = useVoyadaFlag()

const zohoAvailable = ref(false)

// How often (ms) to poll for Zoho/Voyada DOM elements after mount
const CHAT_OBSERVER_POLL_INTERVAL = 500
// Max number of poll attempts before giving up
const CHAT_OBSERVER_MAX_ATTEMPTS = 20

const scrolledDown = ref(false)
const showAccountSheet = showMobileAccountSheet
const agentLevel = ref(1)
let hasLoadedAgentLevel = false
let zohoObserver: MutationObserver | null = null
let voyadaObserver: MutationObserver | null = null

// Mobile nav reserves 79px at the viewport bottom on mobile widths. Expose this
// as a CSS variable so other fixed/anchored elements (booking footer, cart panel,
// detail price box, etc.) can offset themselves only when MobileNav is mounted.
const MOBILE_NAV_HEIGHT_PX = 79
const MOBILE_NAV_BREAKPOINT = 751
const cssVarRoot = typeof document !== 'undefined' ? document.documentElement : null
const { width: windowWidth } = useWindowSize()

function applyMobileNavHeightVar(width: number) {
  if (!cssVarRoot) return
  const value = width < MOBILE_NAV_BREAKPOINT ? `${MOBILE_NAV_HEIGHT_PX}px` : '0px'
  cssVarRoot.style.setProperty('--mobile-nav-height', value)
}

function clearMobileNavHeightVar() {
  cssVarRoot?.style.setProperty('--mobile-nav-height', '0px')
}

watch(windowWidth, (w) => applyMobileNavHeightVar(w))

function onScroll() {
  scrolledDown.value = window.scrollY > 0
}

onMounted(() => {
  applyMobileNavHeightVar(windowWidth.value)
  window.__ttgZohoSalesIq?.setCustomLauncher(true)
  zohoAvailable.value = !!window.__ttgZohoSalesIq
  window.addEventListener('scroll', onScroll, { passive: true })
  loadAgentLevel()

  // Watch for Zoho being closed via its own UI (not our toggle)
  const watchZoho = () => {
    const el = document.querySelector('#zsiq_chat_wrap') as HTMLElement | null
    if (!el) return
    zohoObserver = new MutationObserver(() => {
      if (el.style.display === 'none') onZohoClosed()
    })
    zohoObserver.observe(el, { attributes: true, attributeFilter: ['style'] })
  }

  // Watch for Voyada being opened/closed via its own UI (not our toggle)
  const watchVoyada = () => {
    const el = document.querySelector('#voyada-drawer')
    if (!el) return
    let wasOpen = el.classList.contains('is-open')
    voyadaObserver = new MutationObserver(() => {
      const isOpen = el.classList.contains('is-open')
      if (isOpen === wasOpen) return
      wasOpen = isOpen
      if (isOpen) onVoyadaOpened()
      else onVoyadaClosed()
    })
    voyadaObserver.observe(el, {
      attributes: true,
      attributeFilter: ['class']
    })
  }

  let attempts = 0
  const poll = setInterval(() => {
    if (!zohoObserver) watchZoho()
    if (!voyadaObserver) watchVoyada()
    if ((zohoObserver && voyadaObserver) || ++attempts > CHAT_OBSERVER_MAX_ATTEMPTS)
      clearInterval(poll)
  }, CHAT_OBSERVER_POLL_INTERVAL)
})

onUnmounted(() => {
  window.removeEventListener('scroll', onScroll)
  zohoObserver?.disconnect()
  voyadaObserver?.disconnect()
  clearAccountTierClass()
  clearMobileNavHeightVar()
})

const isDark = computed(
  () => route.path === '/' && !scrolledDown.value && voyadaHeroMode.value === 'voyada'
)

const accountDisplayName = computed(() => {
  const lastName = String(userStore.user.lastName || '').trim()
  const firstName = String(userStore.user.firstName || '').trim()
  const customerName = String(userStore.user.customerName || '').trim()

  if (lastName && firstName) return `${lastName}/ ${firstName}`
  if (lastName || firstName) return lastName || firstName
  return customerName || t('mobileNav.account')
})

const accountAvatarUrl = computed(() => {
  const avatar = String(userStore.user.avatar || '').trim()
  if (!avatar || isDefaultAvatarPath(avatar)) return ''
  return dataUrl(avatar)
})

const hasAccountAvatar = computed(() => accountAvatarUrl.value !== '')

function isDefaultAvatarPath(avatar: string) {
  const normalized = avatar.toLowerCase()
  return normalized.includes('/images/icon_user') || normalized.includes('/images/icon_user_b')
}

const accountInitials = computed(() => {
  const firstName = String(userStore.user.firstName || '').trim()
  const lastName = String(userStore.user.lastName || '').trim()
  const customerName = String(userStore.user.customerName || '').trim()
  const email = String(userStore.user.email || '').trim()
  const source = `${lastName} ${firstName}`.trim() || customerName || email || 'U'
  const words = source.split(/[\s/._-]+/).filter(Boolean)

  if (words.length >= 2) {
    return `${words[0][0] || ''}${words[1][0] || ''}`.toUpperCase()
  }

  return (words[0]?.slice(0, 2) || 'U').toUpperCase()
})

const accountAvatarUploadAction = computed(() => {
  const customerId = Number(userStore.user.customerId || 0)
  if (!customerId) return ''
  return `${import.meta.env.VITE_API_URL}tool/upload/upload_img&&return=json&uploaddir=customer&list_id=${customerId}`
})

function handleAccountAvatarSuccess(res: any) {
  if (!res?.success || !res?.data?.url) return

  let avatar = `${res.server || ''}${res.data.url}`
  if (avatar.includes('../')) {
    avatar = avatar.replace('../', '')
  }

  userStore.user.avatar = avatar
}

const accountCustomerGroupId = computed(() => {
  const groupId = Number.parseInt(String(userStore.user.customerGroupId ?? 0), 10)
  return Number.isFinite(groupId) ? groupId : 0
})

const isAgentAccount = computed(() => accountCustomerGroupId.value === 3 || accountCustomerGroupId.value === 4)

const accountAgencyId = computed(() => String(userStore.user.accountId || '').trim())

function calculateAgentLevel(rates: any[], yearTotal: unknown) {
  let level = 1
  const total = Number(yearTotal || 0)
  const thresholds = rates
    .map((rate) => Number(rate?.paid_total || 0))
    .filter((threshold) => Number.isFinite(threshold) && threshold > 0)
    .sort((a, b) => a - b)

  thresholds.forEach((threshold) => {
    if (total >= threshold) {
      level += 1
    }
  })

  if (thresholds.length === 0 && rates.length > 0) {
    level = 1
  }

  agentLevel.value = Math.min(Math.max(level, 1), 5)
}

function loadAgentLevel() {
  if (!isAgentAccount.value || hasLoadedAgentLevel) return
  hasLoadedAgentLevel = true

  new Request().webPost(
    {},
    (rateRes) => {
      const rates = Array.isArray(rateRes?.rates_list) ? rateRes.rates_list : []
      new Request().webPost(
        {},
        (totalRes) => {
          calculateAgentLevel(rates, totalRes?.year_total)
        },
        'account/agent_edit/getYearlTotal'
      )
    },
    'account/agent_edit/getCommissionRate'
  )
}

watch(isAgentAccount, (agent) => {
  if (agent) loadAgentLevel()
})

const accountMembershipType = computed(() => {
  if (isAgentAccount.value) return 0

  const history = (userStore.user.mmshipHistory as any)?.[0] || {}
  const rawType = userStore.user.memberType ?? history.mmshiptypeid ?? 0
  const typeId = Number.parseInt(String(rawType), 10)
  return Number.isFinite(typeId) ? typeId : 0
})

const accountMembershipLabel = computed(() => {
  if (isAgentAccount.value) return ''

  const typeId = accountMembershipType.value
  if (!typeId) return ''

  const history = (userStore.user.mmshipHistory as any)?.[0] || {}
  const rawName = String(
    userStore.user.memberTypeName ||
      history.membership_type_name ||
      history.name ||
      history.description ||
      history.membershipname ||
      history.membership_name ||
      history.membership_type_name ||
      ''
  ).trim()
  return `${rawName || 'Member'} member`
})

const accountMembershipIcon = computed(() => {
  const typeId = accountMembershipType.value
  return typeId ? `/images/refer/vip${typeId}.png` : '/images/refer/vip.png'
})

const accountTierName = computed(() => {
  if (isAgentAccount.value) return 'agent'

  const tierClassMap: Record<number, string> = {
    1: 'insider',
    2: 'voyager',
    3: 'adventurer',
    4: 'elite',
    10: 'discoverer'
  }

  const typeId = accountMembershipType.value
  if (!typeId) return ''
  return tierClassMap[typeId] || 'discoverer'
})

const accountSheetClass = computed(() => {
  if (!accountTierName.value) return 'mobile-account-sheet--guest'
  return ['mobile-account-sheet--member', `mobile-account-sheet--${accountTierName.value}`]
})

const accountTierClassNames = [
  'mobile-account-sheet-active',
  'mobile-account-tier-active',
  'mobile-account-tier-insider',
  'mobile-account-tier-voyager',
  'mobile-account-tier-adventurer',
  'mobile-account-tier-elite',
  'mobile-account-tier-discoverer',
  'mobile-account-tier-agent'
]

function clearAccountTierClass() {
  document.documentElement.classList.remove(...accountTierClassNames)
}

watch([showAccountSheet, accountTierName], ([open, tier]) => {
  clearAccountTierClass()
  if (!open) return
  document.documentElement.classList.add('mobile-account-sheet-active')
  if (!tier) return
  document.documentElement.classList.add('mobile-account-tier-active', `mobile-account-tier-${tier}`)
})

// Tracks which nav item triggered the login modal so we can toggle it closed
const loginRequestedBy = ref<string | null>(null)

const hasActiveOverlay = computed(
  () =>
    cart.showCartPanel ||
    showAccountSheet.value ||
    showMobileLeftMenu.value ||
    loginRequestedBy.value !== null ||
    activeChat.value !== null
)

function dismissModals() {
  if (activeChat.value === 'voyada') toggleVoyada()
  else if (activeChat.value === 'support') toggleSupport()
  loginRequestedBy.value = null
  userStore.closeLoginRequest()
  cart.closeCart()
  showAccountSheet.value = false
  showMobileLeftMenu.value = false
}

function requireAuth(key: string, action: () => void) {
  // Same item clicked again while login modal is open → close it
  if (loginRequestedBy.value === key) {
    dismissModals()
    return
  }
  dismissModals()
  if (userStore.user.isLogin) {
    action()
  } else {
    loginRequestedBy.value = key
    userStore.requestLogin((ok) => {
      loginRequestedBy.value = null
      if (ok) action()
    })
  }
}

const allItems = computed(() => [
  {
    id: 'home',
    label: t('mobileNav.home'),
    active: route.path === '/' && !hasActiveOverlay.value,
    action: () => {
      dismissModals()
      if (route.path !== '/') window.location.href = '/'
    },
    badge: 0
  },
  {
    id: 'cart',
    label: t('mobileNav.cart'),
    active: loginRequestedBy.value === 'cart' || cart.showCartPanel,
    action: () => {
      if (cart.showCartPanel) { cart.closeCart(); return }
      requireAuth('cart', () => cart.openCart())
    },
    badge: cart.count > 0 ? cart.count : 0
  },
  {
    id: 'voyada',
    label: t('mobileNav.aiChat'),
    active: activeChat.value === 'voyada',
    action: () => {
      if (activeChat.value === 'support') toggleSupport()
      loginRequestedBy.value = null
      userStore.closeLoginRequest()
      cart.closeCart()
      showMobileLeftMenu.value = false
      showAccountSheet.value = false
      toggleVoyada()
    },
    badge: 0
  },
  {
    id: 'account',
    label: t('mobileNav.account'),
    active: loginRequestedBy.value === 'account' || showAccountSheet.value || showMobileLeftMenu.value,
    action: () => {
      if (showAccountSheet.value) { showAccountSheet.value = false; return }
      requireAuth('account', () => {
        showAccountSheet.value = true
      })
    },
    badge: 0
  },
  {
    id: 'support',
    label: t('mobileNav.support'),
    active: activeChat.value === 'support',
    disabled: !zohoAvailable.value,
    action: () => {
      if (!zohoAvailable.value) return
      if (activeChat.value === 'voyada') toggleVoyada()
      loginRequestedBy.value = null
      userStore.closeLoginRequest()
      cart.closeCart()
      showMobileLeftMenu.value = false
      showAccountSheet.value = false
      toggleSupport()
    },
    badge: 0
  }
])

const visibleItems = computed(() =>
  allItems.value.filter((item) => item.id !== 'voyada' || voyadaEnabled.value)
)
</script>

<style scoped>
.mobile-nav {
  display: none;
}

@media screen and (max-width: 751px) {
  .mobile-nav {
    display: flex;
    flex-direction: row;
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 79px;
    z-index: 10000;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scroll-snap-type: x mandatory;
  }

  .mobile-nav--light {
    background: #fff;
    border-top: 1px solid #eaeaea;
  }

  .mobile-nav--dark {
    background: #000;
    border-top: 1px solid #333;
  }

  .mobile-nav__item {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 12px 16px 18px 16px;
    gap: 8px;
    flex: 1 0 0;
    min-width: 56px;
    cursor: pointer;
    background: none;
    border: none;
    scroll-snap-align: start;
    transition: opacity 0.15s;
  }

  .mobile-nav--light .mobile-nav__item {
    color: #666;
  }

  .mobile-nav--dark .mobile-nav__item {
    color: #aaa;
  }

  .mobile-nav__item--disabled {
    opacity: 0.35;
    cursor: not-allowed;
    pointer-events: none;
  }

  .mobile-nav__item--active {
    position: relative;
  }

  .mobile-nav__item--active::before {
    content: '';
    position: absolute;
    inset: 6px 8px auto 8px;
    height: 32px;
    border-radius: 16px;
    background: rgba(16, 157, 148, 0.12);
  }

  .mobile-nav--dark .mobile-nav__item--active::before {
    background: rgba(76, 205, 153, 0.15);
  }

  .mobile-nav--light .mobile-nav__item--active {
    color: #109d94;
  }

  .mobile-nav--dark .mobile-nav__item--active {
    color: #4ccd99;
  }

  .mobile-nav__icon {
    position: relative;
    width: 24px;
    height: 24px;
    aspect-ratio: 1 / 1;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .mobile-nav__close-pill {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: #109d94;
    color: #fff;
  }

  .mobile-nav__badge {
    position: absolute;
    top: -4px;
    right: -6px;
    background: #ff5722;
    color: #fff;
    font-size: 9px;
    font-weight: 600;
    line-height: 1;
    min-width: 14px;
    height: 14px;
    border-radius: 7px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 3px;
  }

  .mobile-nav--dark .mobile-nav__voyada-icon--active {
    filter: brightness(0) invert(1);
  }

  .mobile-nav__label {
    font-size: 10px;
    font-weight: 500;
    line-height: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
  }

  .mobile-nav__item--active .mobile-nav__label {
    font-weight: 700;
  }
}
</style>

<!-- Hide Zoho launcher and Voyada FAB on mobile — replaced by MobileNav -->
<style scoped>
@media screen and (max-width: 1280px) {
  .mobile-account-sheet {
    position: fixed;
    inset: 56px 0 var(--mobile-nav-height, 0px) 0;
    z-index: 9999;
    background: #fff;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .mobile-account-sheet--member {
    background: var(--mobile-account-tier-bg, #c7d3e4);
  }

  .mobile-account-sheet--insider {
    --mobile-account-tier-bg: linear-gradient(180deg, #c8d4e5 0%, #edf4ff 100%);
    --mobile-account-avatar-bg: #b7c6db;
  }

  .mobile-account-sheet--agent {
    --mobile-account-tier-bg: linear-gradient(180deg, #c8d4e5 0%, #edf4ff 100%);
    --mobile-account-avatar-bg: #b7c6db;
  }

  .mobile-account-sheet--voyager {
    --mobile-account-tier-bg: linear-gradient(180deg, #6799f4 0%, #f3f7ff 100%);
    --mobile-account-avatar-bg: #5f95f2;
  }

  .mobile-account-sheet--adventurer {
    --mobile-account-tier-bg: linear-gradient(180deg, #d173ef 0%, #fbf1ff 100%);
    --mobile-account-avatar-bg: #c55ee9;
  }

  .mobile-account-sheet--elite {
    --mobile-account-tier-bg: linear-gradient(180deg, #ff8f72 0%, #fff1eb 100%);
    --mobile-account-avatar-bg: #ff7f61;
  }

  .mobile-account-sheet--discoverer {
    --mobile-account-tier-bg: linear-gradient(180deg, #27633f 0%, #edf6ef 100%);
    --mobile-account-avatar-bg: #39704c;
  }

  .mobile-account-sheet__header {
    display: none;
  }

  .mobile-account-sheet__body {
    flex: 1;
    overflow-y: auto;
    padding: 3.2rem 2rem 2.4rem;
    box-sizing: border-box;
  }

  .mobile-account-sheet--member .mobile-account-sheet__body {
    padding: 8.8rem 1.6rem 2.4rem;
  }

  .mobile-account-profile {
    margin-bottom: 2.8rem;
  }

  .mobile-account-sheet--member .mobile-account-profile {
    position: relative;
    margin: 0;
    padding: 6.8rem 1.6rem 2.8rem;
    background: #fff;
    border-radius: 2.4rem 2.4rem 0 0;
  }

  .mobile-account-profile__welcome {
    margin: 0 0 2.4rem;
    font-family: Poppins, sans-serif;
    font-size: 2rem;
    font-weight: 600;
    line-height: 1.35;
    color: #333;
  }

  .mobile-account-sheet--member .mobile-account-profile__welcome {
    display: none;
  }

  .mobile-account-profile__user {
    display: flex;
    align-items: center;
    gap: 2.4rem;
    min-width: 0;
  }

  .mobile-account-sheet--member .mobile-account-profile__user {
    flex-direction: column;
    justify-content: center;
    gap: 1.4rem;
    text-align: center;
  }

  .mobile-account-profile__avatar-wrap {
    position: relative;
    width: 5.4rem;
    height: 5.4rem;
    flex-shrink: 0;
  }

  .mobile-account-sheet--member .mobile-account-profile__avatar-wrap {
    position: absolute;
    top: -5.4rem;
    left: 50%;
    width: 10.8rem;
    height: 10.8rem;
    transform: translateX(-50%);
  }

  .mobile-account-profile__avatar {
    width: 5.4rem;
    height: 5.4rem;
    border-radius: 50%;
    background: #fff;
    object-fit: cover;
    display: block;
  }

  .mobile-account-profile__avatar-fallback {
    background: var(--mobile-account-avatar-bg, #4f6f52);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: Poppins, sans-serif;
    font-size: 2rem;
    font-weight: 700;
    line-height: 1;
    letter-spacing: 0;
  }

  .mobile-account-sheet--member .mobile-account-profile__avatar {
    width: 10.8rem;
    height: 10.8rem;
    border: 0.5rem solid #fff;
    box-shadow: 0 1.2rem 3rem rgba(35, 45, 66, 0.14);
  }

  .mobile-account-sheet--member .mobile-account-profile__avatar-fallback {
    font-size: 3.8rem;
  }

  .mobile-account-profile__avatar-upload {
    position: absolute;
    right: -0.2rem;
    bottom: -0.2rem;
    width: 2.8rem;
    height: 2.8rem;
    border-radius: 50%;
    overflow: hidden;
    z-index: 2;
  }

  .mobile-account-sheet--member .mobile-account-profile__avatar-upload {
    right: 0.7rem;
    bottom: 0.7rem;
    width: 3rem;
    height: 3rem;
  }

  .mobile-account-profile__avatar-edit {
    width: 100%;
    height: 100%;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: transparent;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .mobile-account-profile__avatar-edit img {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
  }

  .mobile-account-profile__meta {
    min-width: 0;
  }

  .mobile-account-profile__name {
    margin: 0;
    font-family: Poppins, sans-serif;
    font-size: 2.4rem;
    font-weight: 500;
    line-height: 1.25;
    color: #333;
    overflow-wrap: anywhere;
  }

  .mobile-account-sheet--member .mobile-account-profile__name {
    font-size: 2rem;
    font-weight: 600;
  }

  .mobile-account-profile__membership {
    display: flex;
    align-items: center;
    gap: 0.8rem;
    margin: 0.4rem 0 0;
    font-family: Poppins, sans-serif;
    font-size: 1.4rem;
    font-weight: 400;
    line-height: 1.35;
    color: #7a7a7a;
  }

  .mobile-account-sheet--member .mobile-account-profile__membership {
    justify-content: center;
    margin-top: 0.8rem;
    font-size: 1.2rem;
    color: #8a8a8a;
  }

  .mobile-account-profile__vip {
    width: 2rem;
    height: 2rem;
    object-fit: contain;
    flex-shrink: 0;
  }

  .mobile-account-profile__agent {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.8rem;
    margin-top: 1rem;
  }

  .mobile-account-profile__agency-id {
    margin: 0;
    font-family: Poppins, sans-serif;
    font-size: 1.3rem;
    font-weight: 500;
    line-height: 1.35;
    color: #8a8a8a;
  }

  .mobile-account-profile__agent-level {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.4rem;
    min-height: 2rem;
  }

  .mobile-account-profile__diamond {
    width: 1.8rem;
    height: 1.8rem;
    object-fit: contain;
    flex-shrink: 0;
  }
}
</style>

<style>
@media screen and (max-width: 1280px) {
  html.mobile-account-tier-active .header-voyada.whitebg,
  html.mobile-account-tier-active .header-voyada.bbg {
    color: #fff !important;
    box-shadow: none !important;
  }

  html.mobile-account-tier-insider .header-voyada.whitebg,
  html.mobile-account-tier-insider .header-voyada.bbg {
    background: #c8d4e5 !important;
  }

  html.mobile-account-tier-agent .header-voyada.whitebg,
  html.mobile-account-tier-agent .header-voyada.bbg {
    background: #c8d4e5 !important;
  }

  html.mobile-account-tier-voyager .header-voyada.whitebg,
  html.mobile-account-tier-voyager .header-voyada.bbg {
    background: #6799f4 !important;
  }

  html.mobile-account-tier-adventurer .header-voyada.whitebg,
  html.mobile-account-tier-adventurer .header-voyada.bbg {
    background: #d173ef !important;
  }

  html.mobile-account-tier-elite .header-voyada.whitebg,
  html.mobile-account-tier-elite .header-voyada.bbg {
    background: #ff8f72 !important;
  }

  html.mobile-account-tier-discoverer .header-voyada.whitebg,
  html.mobile-account-tier-discoverer .header-voyada.bbg {
    background: #27633f !important;
  }

  html.mobile-account-tier-active .header-voyada.whitebg .logo img:first-child,
  html.mobile-account-tier-active .header-voyada.bbg .logo img:first-child {
    display: none !important;
  }

  html.mobile-account-tier-active .header-voyada.whitebg .logo img:last-child,
  html.mobile-account-tier-active .header-voyada.bbg .logo img:last-child {
    display: block !important;
  }

  .mobile-account-sheet__body .menu {
    padding: 0 0 2.4rem !important;
  }

  .mobile-account-sheet--member .mobile-account-sheet__body .menu {
    margin: 0;
    padding: 0 1.6rem 2rem !important;
    background: #fff;
    border-top: 1px solid #ececec;
    border-radius: 0 0 2.4rem 2.4rem;
    box-shadow: 0 1.6rem 3rem rgba(35, 45, 66, 0.06);
  }

  .mobile-account-sheet__body .menu .r {
    min-height: 6rem;
    display: flex;
    align-items: center;
    gap: 1.8rem;
    padding: 0;
    font-family: Poppins, sans-serif;
    font-size: 1.6rem !important;
    font-weight: 400 !important;
    line-height: 1.35;
    color: #444;
    border-bottom: 1px solid #ececec;
  }

  .mobile-account-sheet--member .mobile-account-sheet__body .menu .r {
    min-height: 5.2rem;
    gap: 1.6rem;
    font-size: 1.4rem !important;
  }

  .mobile-account-sheet__body .menu .r:last-child {
    border-bottom: 0;
  }

  .mobile-account-sheet__body .menu .r img {
    width: 2.4rem;
    height: 2.4rem;
    margin-right: 0;
    object-fit: contain;
    flex-shrink: 0;
  }

  .mobile-account-sheet--member .mobile-account-sheet__body .menu .r img {
    width: 2rem;
    height: 2rem;
  }

  .mobile-account-sheet__body .menu .userline {
    display: none !important;
  }

  html.mobile-account-sheet-active #voyada-fab {
    display: none !important;
  }
}

@media screen and (max-width: 751px) {
  /* Login modal — full screen minus nav bar.
     z-index must clear register.vue's .darkbg (999998) — that backdrop is scoped
     to register.vue with its own inflated z-index, so this global mobile override
     has to sit above it too or the signup form renders under a grey overlay. */
  .loginpage {
    position: fixed !important;
    inset: 0 0 var(--mobile-nav-height, 0px) 0 !important;
    width: 100% !important;
    height: auto !important;
    top: 0 !important;
    left: 0 !important;
    transform: none !important;
    border-radius: 0 !important;
    overflow-y: auto !important;
    z-index: 1000000 !important;
  }

  /* Cart panel — full screen minus nav bar */
  .cart-panel {
    position: fixed !important;
    inset: 0 0 var(--mobile-nav-height, 0px) 0 !important;
    width: 100% !important;
    height: auto !important;
    max-height: none !important;
    bottom: var(--mobile-nav-height, 0px) !important;
    right: 0 !important;
    border-radius: 0 !important;
    overflow-y: auto !important;
  }
}
</style>

<!-- Hide Zoho launcher and Voyada FAB on mobile — replaced by MobileNav -->
<style>
/* Voyada drawer: when closed, fully remove it from layout + hit-testing.
   The SDK only sets opacity:0 + pointer-events:none on close, leaving the embed
   <iframe> as a full-viewport position:fixed layer. On iOS WebKit an iframe
   ignores an ancestor's pointer-events:none for touch-scroll, so the
   closed-but-present iframe silently swallows page scroll. display:none keeps
   the iframe in the DOM (chat state preserved) but takes it out of hit-testing. */
#voyada-drawer:not(.is-open) {
  display: none !important;
}

@media screen and (max-width: 751px) {
  .zsiq-custom-stickercont,
  #zsiq_float,
  .zsiq_flt_rel,
  .siqico-chat,
  #voyada-fab {
    display: none !important;
  }

  /* Keep chat panels below the nav bar */
  #zsiq_chat_wrap,
  .zls-sptwndw {
    z-index: 9999 !important;
    bottom: var(--mobile-nav-height, 0px) !important;
  }

  /* Voyada drawer: full-screen above nav bar, above account sheet */
  #voyada-drawer.is-open {
    z-index: 10001 !important;
    position: fixed !important;
    inset: 0 0 var(--mobile-nav-height, 0px) 0 !important;
    width: 100% !important;
    height: auto !important;
    max-height: none !important;
    border-radius: 0 !important;
    right: 0 !important;
  }
}
</style>
